import { getInternalAxiosInstance } from 'src/data/api/api';
import BaseFetchOptions from 'src/data/api/common/BaseFetchOptions';
import QueryParam from 'src/data/api/common/QueryParam';
import { getQueryParams } from 'src/data/api/endpointUrlBuilder';
import ApiResponse from 'src/data/api/responses/ApiResponse';
import ApiResponseBody from 'src/data/api/responses/ApiResponseBody';
import PaginatedApiResponseBody from 'src/data/api/responses/PaginatedApiResponseBody';
import {
    TICKET_LINK_IMPORT_APPLY_ENDPOINT,
    TICKET_LINK_IMPORT_CREATE_ENDPOINT,
    TICKET_LINK_IMPORT_GET_BY_ID_ENDPOINT,
    TICKET_LINK_IMPORT_GET_ENDPOINT,
    TICKET_LINK_IMPORT_REMATCH_FAILED_ROWS_ENDPOINT,
    TICKET_LINK_IMPORT_ROW_ACCESS_LINK_ENDPOINT,
    TICKET_LINK_IMPORT_ROW_DUPLICATE_TICKET_ENDPOINT,
    TICKET_LINK_IMPORT_ROWS_GET_ENDPOINT,
} from 'src/data/constants/endpoints/ticket-link-import-endpoints';
import { EmptyBody } from 'src/data/models/common/emptyBody';
import TicketLinkImport, {
    TicketLinkImportRow,
    TicketLinkImportRowsPaginationMeta,
    TicketLinkImportRowStatus,
} from 'src/data/models/ticket-link-import/TicketLinkImport';
import EventTicketFile from 'src/data/models/tickets/eventTicketFileWithFileNames';
import { TicketDownloadLinkResponse } from 'src/data/services/ticketService';

async function createTicketLinkImport(
    bookingOptionId: string,
    file: File
): Promise<ApiResponse<ApiResponseBody<EmptyBody>>> {
    const formData = new FormData();

    formData.append('bookingOptionId', bookingOptionId);
    formData.append('file', file);

    return await getInternalAxiosInstance()
        .post<ApiResponseBody<EmptyBody>>(TICKET_LINK_IMPORT_CREATE_ENDPOINT, formData)
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function getTicketLinkImport(
    ticketLinkImportId: string
): Promise<ApiResponse<ApiResponseBody<TicketLinkImport>>> {
    return await getInternalAxiosInstance()
        .get<ApiResponseBody<TicketLinkImport>>(
            TICKET_LINK_IMPORT_GET_BY_ID_ENDPOINT(ticketLinkImportId)
        )
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function getTicketLinkImports(
    bookingOptionId: string,
    options?: BaseFetchOptions
): Promise<ApiResponse<PaginatedApiResponseBody<TicketLinkImport>>> {
    return await getInternalAxiosInstance()
        .get<PaginatedApiResponseBody<TicketLinkImport>>(
            TICKET_LINK_IMPORT_GET_ENDPOINT(bookingOptionId),
            {
                params: options && getQueryParams(options),
            }
        )
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function getTicketLinkImportRows(
    ticketLinkImportId: string,
    failedRowsOnly: boolean,
    options?: BaseFetchOptions
): Promise<
    ApiResponse<PaginatedApiResponseBody<TicketLinkImportRow, TicketLinkImportRowsPaginationMeta>>
> {
    let extraQueryParams: QueryParam[] = [];

    if (failedRowsOnly) {
        let failedStatuses: TicketLinkImportRowStatus[] = ['MatchingFailed', 'ApplyFailed'];

        extraQueryParams.push({
            key: 'filter[status]',
            value: failedStatuses.join(','),
        });
    }

    return await getInternalAxiosInstance()
        .get<PaginatedApiResponseBody<TicketLinkImportRow, TicketLinkImportRowsPaginationMeta>>(
            TICKET_LINK_IMPORT_ROWS_GET_ENDPOINT(ticketLinkImportId),
            {
                params: options && getQueryParams(options, extraQueryParams),
            }
        )
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function applyTicketLinkImport(
    ticketLinkImportId: string
): Promise<ApiResponse<ApiResponseBody<EmptyBody>>> {
    return await getInternalAxiosInstance()
        .post<ApiResponseBody<EmptyBody>>(TICKET_LINK_IMPORT_APPLY_ENDPOINT(ticketLinkImportId))
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function accessTicketLinkImportRowLink(
    importRowId: string
): Promise<ApiResponse<ApiResponseBody<TicketDownloadLinkResponse>>> {
    return await getInternalAxiosInstance()
        .post<ApiResponseBody<TicketDownloadLinkResponse>>(
            TICKET_LINK_IMPORT_ROW_ACCESS_LINK_ENDPOINT(importRowId)
        )
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function getTicketLinkDuplicateTicketId(
    importRowId: string
): Promise<ApiResponse<ApiResponseBody<EventTicketFile>>> {
    return await getInternalAxiosInstance()
        .get<ApiResponseBody<EventTicketFile>>(
            TICKET_LINK_IMPORT_ROW_DUPLICATE_TICKET_ENDPOINT(importRowId)
        )
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

async function rematchFailedTicketLinkImportRows(
    ticketLinkImportId: string
): Promise<ApiResponse<ApiResponseBody<EmptyBody>>> {
    return await getInternalAxiosInstance()
        .post<ApiResponseBody<EmptyBody>>(
            TICKET_LINK_IMPORT_REMATCH_FAILED_ROWS_ENDPOINT(ticketLinkImportId)
        )
        .then((response) => ({
            data: response.data,
            headers: response.headers,
            statusCode: response.status,
        }));
}

export default {
    createTicketLinkImport,
    getTicketLinkImport,
    getTicketLinkImports,
    getTicketLinkImportRows,
    applyTicketLinkImport,
    accessTicketLinkImportRowLink,
    getTicketLinkDuplicateTicketId,
    rematchTicketLinkImport: rematchFailedTicketLinkImportRows,
};
